import { useCallback, useEffect, useRef, useState } from "react";

type Result = {
    /**
     * Callback ref — attach to the element whose overflow you want to
     * track. Callback refs run on mount AND whenever React swaps the
     * underlying DOM node (e.g. conditional render flipping between two
     * trees), which a `useRef`-style object ref wouldn't pick up because
     * its identity is stable and any `useEffect` keyed on it wouldn't
     * re-run.
     */
    ref: (node: HTMLElement | null) => void;
    isTruncated: boolean;
};

/**
 * Returns true when the referenced element's content overflows its
 * visible width — i.e. CSS `truncate` is actually clipping. The element
 * is observed with ResizeObserver so container resizes (resizable panels,
 * sidebar toggle, viewport changes) re-measure for free. Pass the current
 * text as `text` so content-only changes (where the box stays the same
 * size) also trigger a re-measure.
 */
export function useIsTruncated(text?: string): Result {
    const [isTruncated, setIsTruncated] = useState(false);
    const nodeRef = useRef<HTMLElement | null>(null);
    const observerRef = useRef<ResizeObserver | null>(null);

    const measure = useCallback(() => {
        const node = nodeRef.current;
        if (!node) return;
        setIsTruncated(node.scrollWidth > node.clientWidth);
    }, []);

    const ref = useCallback(
        (node: HTMLElement | null) => {
            observerRef.current?.disconnect();
            observerRef.current = null;
            nodeRef.current = node;

            if (!node) {
                setIsTruncated(false);
                return;
            }

            measure();

            if (typeof ResizeObserver !== "undefined") {
                const observer = new ResizeObserver(measure);
                observer.observe(node);
                observerRef.current = observer;
            }
        },
        [measure]
    );

    // Re-measure on text changes (ResizeObserver only fires on box
    // resize; pure content swaps inside a same-size container won't
    // trigger it).
    useEffect(() => {
        measure();
    }, [measure, text]);

    // Clean up on unmount.
    useEffect(() => {
        return () => {
            observerRef.current?.disconnect();
            observerRef.current = null;
        };
    }, []);

    return { ref, isTruncated };
}
